/**
 * src/components/consent/ConsentProvider.tsx
 *
 * Çerez onay durumunu paylaşan React context. CookieBanner onayı yazar;
 * GoogleAnalytics onayı okuyarak gtag'i yalnızca analytics izni varsa yükler.
 *
 * Client Component — localStorage/cookie erişimi + reaktif state gerekir.
 * `decided=false` iken (henüz karar yok) banner gösterilir.
 */

'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import {
  CONSENT_VERSION,
  readConsent,
  writeConsent,
  type ConsentState,
} from '@/lib/consent/consent'

/** Onay seçimi — necessary hariç (her zaman true) kullanıcı kontrollü kategoriler. */
export interface ConsentChoice {
  analytics: boolean
  marketing: boolean
}

interface ConsentContextValue {
  /** Mevcut onay durumu; null = henüz karar verilmemiş. */
  consent: ConsentState | null
  /** Kullanıcı bir karar verdi mi (banner gizlenir). */
  decided: boolean
  /** Onayı kaydet (localStorage+cookie) + gtag consent güncelle. */
  save: (choice: ConsentChoice, source?: ConsentState['source']) => void
  /** Banner'ı tekrar açmak için onayı sıfırla (footer "çerez ayarları" linki). */
  reopen: () => void
}

const ConsentContext = createContext<ConsentContextValue | null>(null)

/**
 * Google Consent Mode v2 sinyali — gtag yüklüyse analytics_storage durumunu
 * günceller (onay sonrası/revoke). gtag yoksa no-op.
 */
function syncGtagConsent(choice: ConsentChoice): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return
  window.gtag('consent', 'update', {
    analytics_storage: choice.analytics ? 'granted' : 'denied',
    ad_storage: choice.marketing ? 'granted' : 'denied',
    ad_user_data: choice.marketing ? 'granted' : 'denied',
    ad_personalization: choice.marketing ? 'granted' : 'denied',
  })
}

/**
 * ConsentProvider — onay durumunu sağlar. Mount'ta saklı onayı okur.
 */
export function ConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<ConsentState | null>(null)
  // Hydration uyumu: sunucuda null; client mount'ta gerçek değer okunur.
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    setConsent(readConsent())
    setHydrated(true)
  }, [])

  const save = useCallback(
    (choice: ConsentChoice, source: ConsentState['source'] = 'banner') => {
      const next: ConsentState = {
        version: CONSENT_VERSION,
        necessary: true,
        analytics: choice.analytics,
        marketing: choice.marketing,
        timestamp: new Date().toISOString(),
        source,
      }
      writeConsent(next)
      setConsent(next)
      syncGtagConsent(choice)
    },
    [],
  )

  const reopen = useCallback(() => {
    setConsent(null)
  }, [])

  const value = useMemo<ConsentContextValue>(
    () => ({
      consent,
      // Karar verilmiş sayılır ancak hidrasyon tamamlandıktan SONRA — SSR'da
      // banner render edilmez (hydration mismatch önlenir).
      decided: !hydrated || consent !== null,
      save,
      reopen,
    }),
    [consent, hydrated, save, reopen],
  )

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
}

/**
 * useConsent — onay context'ine erişim. Provider dışında çağrılırsa hata.
 */
export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext)
  if (!ctx) throw new Error('useConsent must be used within ConsentProvider')
  return ctx
}
